/**
 * Profile management page — name, avatar, connected accounts,
 * passkeys, and sessions. Shared by admin and learn zones (shell route).
 *
 * @module routes/profile
 */

import { removeAccount } from '@learnai/api/accounts';
import { useAuth } from '@learnai/api/auth-provider';
import { storage } from '@learnai/api/storage';
import { uploadAvatar } from '@learnai/api/upload';
import { formatDateLong, formatDateTimeLong } from '@learnai/tools/temporal';
import { tryCatch } from '@learnai/tools/try-catch';
import { BrandLogo } from '@learnai/ui/components/blocks/brand-logo';
import { ThemePicker } from '@learnai/ui/components/blocks/theme-picker';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@learnai/ui/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@learnai/ui/components/ui/avatar';
import { Badge } from '@learnai/ui/components/ui/badge';
import { Button } from '@learnai/ui/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@learnai/ui/components/ui/card';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@learnai/ui/components/ui/dialog';
import { Input } from '@learnai/ui/components/ui/input';
import { Label } from '@learnai/ui/components/ui/label';
import { toast } from '@learnai/ui/components/ui/sonner';
import { Spinner } from '@learnai/ui/components/ui/spinner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GoogleIcon } from '@web/assets/brand-icons';
import { api, orpc } from '@web/lib/api';
import { getUserInitials } from '@web/lib/avatar';
import { arrayBufferToBase64url, base64urlToArrayBuffer } from '@web/lib/webauthn';
import {
	ArrowLeftIcon,
	CakeIcon,
	CopyIcon,
	ImagePlusIcon,
	KeyIcon,
	Link2Icon,
	LogOutIcon,
	MonitorIcon,
	PencilIcon,
	PlusIcon,
	SettingsIcon,
	Trash2Icon,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import UAParser from 'ua-parser-js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Turns a raw User-Agent string into a short, human-readable device description
 * (e.g. "Chrome 120 on Windows" or "Safari on iPhone") for the sessions list.
 */
function formatSessionDevice(userAgent: string | null): string {
	if (!userAgent?.trim()) return 'Unknown device';
	const ua = new UAParser(userAgent);
	const browser = ua.getBrowser();
	const os = ua.getOS();
	const device = ua.getDevice();
	const browserPart = [browser.name, browser.version].filter(Boolean).join(' ') || null;
	const osPart = [os.name, os.version].filter(Boolean).join(' ') || null;
	const isMobileOrTablet = device.type === 'mobile' || device.type === 'tablet';
	const deviceLabel = isMobileOrTablet && device.model ? device.model : device.type || null;
	const location = [osPart, deviceLabel].filter(Boolean).join(' · ');
	if (browserPart && location) return `${browserPart} on ${location}`;
	if (browserPart) return browserPart;
	if (location) return location;
	return 'Unknown device';
}

/** Formats ISO date (YYYY-MM-DD) in natural language (e.g. February 26th, 2026), or returns 'Not set' if empty. */
function formatBirthdayMDYYYY(iso: string | null): string {
	if (!iso?.trim()) return 'Not set';
	try {
		return formatDateLong(iso);
	} catch {
		return 'Not set';
	}
}

/** Returns age in years from ISO date (YYYY-MM-DD), or null if not set. */
function getAgeFromBirthday(iso: string | null): number | null {
	if (!iso?.trim()) return null;
	try {
		const birth = Temporal.PlainDate.from(iso);
		const today = Temporal.Now.plainDateISO();
		let age = today.year - birth.year;
		if (today.month < birth.month || (today.month === birth.month && today.day < birth.day)) {
			age -= 1;
		}
		return age >= 0 ? age : null;
	} catch {
		return null;
	}
}

/** True when the ISO date (YYYY-MM-DD) has the same month and day as today. */
function isBirthdayToday(iso: string | null): boolean {
	if (!iso?.trim()) return false;
	try {
		const birth = Temporal.PlainDate.from(iso);
		const today = Temporal.Now.plainDateISO();
		return today.month === birth.month && today.day === birth.day;
	} catch {
		return false;
	}
}

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

export function Profile() {
	const { user, refreshUser } = useAuth();
	const queryClient = useQueryClient();
	const [searchParams, setSearchParams] = useSearchParams();
	const [disconnectAccountId, setDisconnectAccountId] = useState<string | null>(null);
	const [connectGooglePending, setConnectGooglePending] = useState(false);
	const [removePasskeyId, setRemovePasskeyId] = useState<string | null>(null);
	const [revokeSessionId, setRevokeSessionId] = useState<string | null>(null);
	const [name, setName] = useState('');
	const [avatarUrl, setAvatarUrl] = useState('');
	const [profileDirty, setProfileDirty] = useState(false);
	const [avatarUploading, setAvatarUploading] = useState(false);
	const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
	const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
	const avatarInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		storage.local.get('currentSessionId').then(setCurrentSessionId);
	}, []);

	const { data: profileData, isLoading: profileLoading } = useQuery({
		...orpc.users.getProfile.queryOptions(),
		enabled: !!user,
	});

	const { data: accountsData } = useQuery({
		...orpc.users.listAccounts.queryOptions(),
		enabled: !!user,
	});

	useEffect(() => {
		if (searchParams.get('google') === 'linked') {
			toast.success('Google account connected.');
			const next = new URLSearchParams(searchParams);
			next.delete('google');
			setSearchParams(next);
		}
	}, [searchParams, setSearchParams]);

	const { data: passkeysData, refetch: refetchPasskeys } = useQuery({
		...orpc.users.listPasskeys.queryOptions(),
		enabled: !!user,
	});

	const { data: sessionsData } = useQuery({
		...orpc.auth.listSessions.queryOptions(),
		enabled: !!user,
	});

	useEffect(() => {
		if (profileData?.user && !profileDirty) {
			setName(profileData.user.name ?? '');
			setAvatarUrl(profileData.user.avatarUrl ?? '');
		}
	}, [profileData?.user?.name, profileData?.user?.avatarUrl, profileDirty]);

	const updateProfileMutation = useMutation({
		...orpc.users.updateProfile.mutationOptions(),
		onSuccess: async () => {
			queryClient.invalidateQueries({ queryKey: orpc.users.getProfile.key() });
			await refreshUser();
			setProfileDirty(false);
			toast.success('Profile updated.');
		},
		onError: (err) => {
			toast.error(err instanceof Error ? err.message : 'Failed to update profile.');
		},
	});

	const disconnectAccountMutation = useMutation({
		...orpc.users.disconnectAccount.mutationOptions(),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: orpc.users.listAccounts.key() });
			setDisconnectAccountId(null);
			toast.success('Account disconnected.');
		},
		onError: (err) => {
			toast.error(err instanceof Error ? err.message : 'Failed to disconnect account.');
		},
	});

	const connectGoogleMutation = useMutation({
		...orpc.auth.oidc.linkToken.mutationOptions(),
		onSuccess: (data) => {
			window.location.href = `${window.location.origin}/api/auth/google?link=1&linkToken=${encodeURIComponent(data.linkToken)}`;
		},
		onError: (err) => {
			setConnectGooglePending(false);
			toast.error(err instanceof Error ? err.message : 'Failed to start Connect Google.');
		},
	});

	const removePasskeyMutation = useMutation({
		...orpc.users.removePasskey.mutationOptions(),
		onSuccess: () => {
			refetchPasskeys();
			setRemovePasskeyId(null);
			toast.success('Passkey removed.');
		},
		onError: (err) => {
			toast.error(err instanceof Error ? err.message : 'Failed to remove passkey.');
		},
	});

	const revokeSessionMutation = useMutation({
		...orpc.auth.revokeSession.mutationOptions(),
		onSuccess: async (_data, variables) => {
			const revokedId = variables.id;
			queryClient.invalidateQueries({ queryKey: orpc.auth.listSessions.key() });
			setRevokeSessionId(null);

			const currentSessionId = await storage.local.get('currentSessionId');
			if (currentSessionId === revokedId && user) {
				await storage.local.delete('accessToken');
				await storage.local.delete('refreshToken');
				await storage.local.delete('currentSessionId');
				await removeAccount(user.id);
				queryClient.clear();
				window.location.href = '/login';
				return;
			}
			toast.success('Session revoked.');
		},
		onError: (err) => {
			toast.error(err instanceof Error ? err.message : 'Failed to revoke session.');
		},
	});

	const logoutAllMutation = useMutation({
		mutationFn: async () => {
			queryClient.clear();
			await api.auth.logoutAll();
		},
		onSuccess: async () => {
			await storage.local.delete('accessToken');
			await storage.local.delete('refreshToken');
			await storage.local.delete('currentSessionId');
			if (user) await removeAccount(user.id);
			window.location.href = '/login';
		},
		onError: (err) => {
			toast.error(err instanceof Error ? err.message : 'Failed to sign out everywhere.');
		},
	});

	const [addPasskeyLoading, setAddPasskeyLoading] = useState(false);
	const addPasskey = useCallback(async () => {
		if (!window.PublicKeyCredential) {
			toast.error('Passkeys are not supported in this browser.');
			return;
		}
		setAddPasskeyLoading(true);
		const { data: optionsData, error: optionsErr } = await tryCatch(api.users.passkeyRegisterOptions());
		if (optionsErr || !optionsData?.options) {
			toast.error(optionsErr instanceof Error ? optionsErr.message : 'Failed to get registration options.');
			setAddPasskeyLoading(false);
			return;
		}
		const optionsJson = optionsData.options as {
			challenge: string;
			user?: { id: string; name: string; displayName: string };
			excludeCredentials?: { id: string; transports?: string[] }[];
			[key: string]: unknown;
		};
		const userEntity =
			optionsJson.user && typeof optionsJson.user.id === 'string'
				? {
						...optionsJson.user,
						id: base64urlToArrayBuffer(optionsJson.user.id),
					}
				: optionsJson.user;
		const options = {
			...optionsJson,
			challenge: base64urlToArrayBuffer(optionsJson.challenge),
			...(userEntity && { user: userEntity }),
			excludeCredentials: optionsJson.excludeCredentials?.map((c) => ({
				type: 'public-key' as const,
				id: base64urlToArrayBuffer(c.id),
				transports: c.transports as AuthenticatorTransport[],
			})),
		} as PublicKeyCredentialCreationOptions;
		let credential: PublicKeyCredential;
		try {
			credential = (await navigator.credentials.create({ publicKey: options })) as PublicKeyCredential;
		} catch (err) {
			if (err instanceof Error && err.name === 'NotAllowedError') {
				toast.info('Registration cancelled.');
			} else {
				toast.error(err instanceof Error ? err.message : 'Failed to create passkey.');
			}
			setAddPasskeyLoading(false);
			return;
		}
		const response = credential.response as AuthenticatorAttestationResponse;
		const credentialJson = {
			id: credential.id,
			rawId: arrayBufferToBase64url(credential.rawId),
			type: 'public-key' as const,
			response: {
				clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
				attestationObject: arrayBufferToBase64url(response.attestationObject),
				transports: response.getTransports?.() ?? [],
			},
		};
		const { error: registerErr } = await tryCatch(api.users.registerPasskey({ credential: credentialJson }));
		if (registerErr) {
			toast.error(registerErr instanceof Error ? registerErr.message : 'Failed to register passkey.');
		} else {
			toast.success('Passkey added.');
			refetchPasskeys();
		}
		setAddPasskeyLoading(false);
	}, [refetchPasskeys]);

	const handleSaveProfile = (e: React.FormEvent) => {
		e.preventDefault();
		updateProfileMutation.mutate({
			name: name.trim() || undefined,
			avatarUrl: avatarUrl.trim() || null,
		});
	};

	const handleCopy = useCallback(async (text: string) => {
		await navigator.clipboard.writeText(text);
		toast.success('Copied to clipboard.');
	}, []);

	const handleAvatarFileChange = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			if (!file) return;
			const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
			if (!allowed.includes(file.type)) {
				toast.error('Please choose a JPEG, PNG, GIF, or WebP image.');
				return;
			}
			if (file.size > 5 * 1024 * 1024) {
				toast.error('Image must be under 5 MB.');
				return;
			}
			setAvatarUploading(true);
			const { data, error } = await tryCatch(uploadAvatar(window.location.origin, file));
			setAvatarUploading(false);
			e.target.value = '';
			if (error) {
				toast.error(error instanceof Error ? error.message : 'Upload failed.');
				return;
			}
			if (data?.url) {
				setAvatarUrl(data.url);
				updateProfileMutation.mutate({ avatarUrl: data.url }, { onSuccess: () => setProfileDirty(false) });
			}
		},
		[updateProfileMutation],
	);

	if (profileLoading || !user) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<Spinner className="size-6 text-muted-foreground" />
			</div>
		);
	}

	const displayName = profileData?.user?.name ?? user.name ?? user.email;
	const profileBirthday = profileData?.user?.birthday ?? null;
	const age = getAgeFromBirthday(profileBirthday);
	const accounts = accountsData?.accounts ?? [];
	const passkeys = passkeysData?.passkeys ?? [];
	const sessions = sessionsData?.sessions ?? [];

	return (
		<div className="relative min-h-screen bg-background">
			<nav className="absolute top-6 left-6">
				<Link to="/">
					<BrandLogo size="sm" />
				</Link>
			</nav>

			<main className="mx-auto max-w-3xl space-y-8 px-4 pt-24 pb-8">
				<div>
					<div className="flex items-center gap-3">
						<Link
							to="/dashboard"
							className="inline-flex shrink-0 items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
						>
							<ArrowLeftIcon className="size-3.5" />
							Back
						</Link>
						<h1 className="text-2xl font-bold">Profile</h1>
					</div>
					<p className="mt-1 text-muted-foreground">Manage your name, photo, accounts, passkeys, and sessions.</p>
				</div>

				{/* Section 1 — Profile */}
				<Card>
					<CardHeader className="flex flex-row items-start gap-4">
						<div className="shrink-0">
							<input
								ref={avatarInputRef}
								type="file"
								accept="image/jpeg,image/png,image/gif,image/webp"
								className="sr-only"
								aria-hidden
								onChange={handleAvatarFileChange}
							/>
							<Button
								type="button"
								variant="ghost"
								className="relative size-auto rounded-full p-0"
								disabled={avatarUploading}
								onClick={() => setAvatarDialogOpen(true)}
								aria-label="Change profile picture"
							>
								<Avatar key={avatarUrl ?? 'no-avatar'} size="lg">
									{avatarUrl ? <AvatarImage src={avatarUrl} alt={displayName} /> : null}
									<AvatarFallback>{getUserInitials(profileData?.user?.name ?? null, user.email)}</AvatarFallback>
								</Avatar>
								<span
									className="absolute bottom-0 right-0 flex size-4 items-center justify-center rounded-full border-2 border-background bg-primary text-primary-foreground"
									aria-hidden
								>
									<PencilIcon className="size-2.5" />
								</span>
							</Button>
						</div>
						<div className="min-w-0 space-y-1.5">
							<CardTitle>Profile</CardTitle>
							<CardDescription>
								Update your display name and profile picture. Email and birthday are read-only.
							</CardDescription>
						</div>
					</CardHeader>
					<CardContent>
						<form onSubmit={handleSaveProfile} className="space-y-6">
							<div className="space-y-4">
								<div className="space-y-2">
									<Label htmlFor="name">Display name</Label>
									<Input
										id="name"
										value={name}
										onChange={(e) => {
											setName(e.target.value);
											setProfileDirty(true);
										}}
										placeholder="Your name"
										maxLength={100}
									/>
								</div>
								<dl className="grid gap-4 sm:grid-cols-1">
									<div>
										<dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Email</dt>
										<dd className="mt-0.5 flex min-w-0 items-center gap-1.5">
											<span className="min-w-0 truncate text-sm font-medium">{user.email}</span>
											<Button
												type="button"
												variant="ghost"
												size="icon-sm"
												className="shrink-0 text-muted-foreground hover:text-foreground"
												onClick={() => handleCopy(user.email)}
												aria-label="Copy email"
											>
												<CopyIcon className="size-3.5" />
											</Button>
										</dd>
									</div>
									<div>
										<dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Birthday</dt>
										<dd className="mt-0.5 flex items-center gap-1.5 text-sm font-medium text-foreground">
											{formatBirthdayMDYYYY(profileBirthday)}
											{age !== null && <span className="text-muted-foreground"> ({age} years old)</span>}
											{isBirthdayToday(profileBirthday) && (
												<CakeIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
											)}
										</dd>
									</div>
								</dl>
							</div>
							<Dialog open={avatarDialogOpen} onOpenChange={setAvatarDialogOpen}>
								<DialogContent showCloseButton={true}>
									<DialogHeader>
										<DialogTitle>Profile Picture</DialogTitle>
										<DialogDescription>
											Upload a new photo or remove your current one. JPEG, PNG, GIF or WebP, max 5 MB.
										</DialogDescription>
									</DialogHeader>
									<DialogFooter showCloseButton={false}>
										<Button
											type="button"
											variant="outline"
											disabled={avatarUploading}
											onClick={() => {
												setAvatarDialogOpen(false);
												avatarInputRef.current?.click();
											}}
										>
											{avatarUploading ? <Spinner className="size-4" /> : <ImagePlusIcon className="size-4" />}
											Upload new avatar
										</Button>
										{avatarUrl ? (
											<Button
												type="button"
												variant="destructive"
												disabled={updateProfileMutation.isPending || avatarUploading}
												onClick={() => {
													updateProfileMutation.mutate(
														{ avatarUrl: null },
														{
															onSuccess: () => {
																setAvatarUrl('');
																setAvatarDialogOpen(false);
															},
														},
													);
												}}
											>
												{updateProfileMutation.isPending ? (
													<Spinner className="size-4" />
												) : (
													<Trash2Icon className="size-4" />
												)}
												{updateProfileMutation.isPending ? 'Removing…' : 'Remove'}
											</Button>
										) : null}
									</DialogFooter>
								</DialogContent>
							</Dialog>
							<Button type="submit" disabled={updateProfileMutation.isPending || !profileDirty}>
								{updateProfileMutation.isPending ? <Spinner className="size-4" /> : null}
								Save changes
							</Button>
						</form>
					</CardContent>
				</Card>

				{/* Settings */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<SettingsIcon className="size-5" />
							Settings
						</CardTitle>
						<CardDescription>Appearance and other preferences.</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="flex items-center justify-between rounded-lg border px-3 py-2">
							<span className="text-sm font-medium">Theme</span>
							<ThemePicker />
						</div>
					</CardContent>
				</Card>

				{/* Section 2 — Connected accounts */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<Link2Icon className="size-5" />
							Connected accounts
						</CardTitle>
						<CardDescription>Linked sign-in providers. You can still sign in with email OTP.</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						{!accounts.some((a) => a.provider === 'google') && (
							<Button
								variant="outline"
								size="sm"
								onClick={() => {
									setConnectGooglePending(true);
									connectGoogleMutation.mutate(undefined);
								}}
								disabled={connectGooglePending || connectGoogleMutation.isPending}
							>
								{connectGooglePending || connectGoogleMutation.isPending ? (
									<Spinner className="size-4" />
								) : (
									<GoogleIcon />
								)}
								<span>Connect Google</span>
							</Button>
						)}
						{accounts.length === 0 ? (
							<p className="text-sm text-muted-foreground">No connected accounts.</p>
						) : (
							<ul className="space-y-2">
								{accounts.map((a) => (
									<li key={a.id} className="flex items-center justify-between rounded-lg border px-3 py-2">
										<span className="font-medium capitalize">{a.provider}</span>
										<Button
											variant="ghost"
											size="sm"
											onClick={() => setDisconnectAccountId(a.id)}
											disabled={disconnectAccountMutation.isPending}
										>
											Disconnect
										</Button>
									</li>
								))}
							</ul>
						)}
					</CardContent>
				</Card>

				{/* Section 3 — Passkeys */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<KeyIcon className="size-5" />
							Passkeys
						</CardTitle>
						<CardDescription>Add or remove passkeys for passwordless sign-in.</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<Button
							variant="outline"
							size="sm"
							onClick={addPasskey}
							disabled={addPasskeyLoading || !window.PublicKeyCredential}
						>
							{addPasskeyLoading ? <Spinner className="size-4" /> : <PlusIcon className="size-4" />}
							Add passkey
						</Button>
						{passkeys.length === 0 ? (
							<p className="text-sm text-muted-foreground">No passkeys registered.</p>
						) : (
							<ul className="space-y-2">
								{passkeys.map((p) => (
									<li key={p.id} className="flex items-center justify-between rounded-lg border px-3 py-2">
										<div>
											<span className="font-medium">{p.name}</span>
											<span className="ml-2 text-xs text-muted-foreground">
												{p.credentialDeviceType === 'multiDevice' ? 'Synced' : 'Device'}
											</span>
										</div>
										<Button
											variant="ghost"
											size="icon"
											onClick={() => setRemovePasskeyId(p.id)}
											disabled={removePasskeyMutation.isPending}
										>
											<Trash2Icon className="size-4" />
										</Button>
									</li>
								))}
							</ul>
						)}
					</CardContent>
				</Card>

				{/* Section 4 — Sessions */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<MonitorIcon className="size-5" />
							Sessions
						</CardTitle>
						<CardDescription>Devices where you are signed in. Revoke any you don’t recognise.</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						{sessions.length === 0 ? (
							<p className="text-sm text-muted-foreground">No active sessions.</p>
						) : (
							<>
								<ul className="space-y-2">
									{sessions.map((s) => (
										<li key={s.id} className="flex items-center justify-between rounded-lg border px-3 py-2">
											<div className="min-w-0 text-sm">
												<div className="flex flex-nowrap items-center gap-2">
													<p className="min-w-0 truncate font-medium">{formatSessionDevice(s.userAgent)}</p>
													{s.id === currentSessionId ? (
														<Badge variant="secondary" className="shrink-0">
															This device
														</Badge>
													) : null}
												</div>
												<p className="text-xs text-muted-foreground">Expires {formatDateTimeLong(s.expiresAt)}</p>
												{s.ipAddress ? <p className="text-xs text-muted-foreground">{s.ipAddress}</p> : null}
											</div>
											<Button
												variant="ghost"
												size="icon"
												onClick={() => setRevokeSessionId(s.id)}
												disabled={revokeSessionMutation.isPending}
												aria-label="Revoke session"
											>
												<LogOutIcon className="size-4" />
											</Button>
										</li>
									))}
								</ul>
								<Button
									variant="destructive"
									size="sm"
									onClick={() => logoutAllMutation.mutate()}
									disabled={logoutAllMutation.isPending}
								>
									{logoutAllMutation.isPending ? <Spinner className="size-4" /> : <LogOutIcon className="size-4" />}
									Sign out everywhere
								</Button>
							</>
						)}
					</CardContent>
				</Card>
			</main>

			{/* Disconnect account confirmation */}
			<AlertDialog open={!!disconnectAccountId} onOpenChange={(open) => !open && setDisconnectAccountId(null)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Disconnect Account?</AlertDialogTitle>
						<AlertDialogDescription>
							You can still sign in with email OTP. This only removes the linked provider.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => disconnectAccountId && disconnectAccountMutation.mutate({ id: disconnectAccountId })}
							disabled={disconnectAccountMutation.isPending}
						>
							{disconnectAccountMutation.isPending ? <Spinner className="size-4" /> : null}
							Disconnect
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Remove passkey confirmation */}
			<AlertDialog open={!!removePasskeyId} onOpenChange={(open) => !open && setRemovePasskeyId(null)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Remove Passkey?</AlertDialogTitle>
						<AlertDialogDescription>
							This passkey will no longer be able to sign in. You can add it again later.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => removePasskeyId && removePasskeyMutation.mutate({ id: removePasskeyId })}
							disabled={removePasskeyMutation.isPending}
						>
							{removePasskeyMutation.isPending ? <Spinner className="size-4" /> : null}
							Remove
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Revoke session confirmation */}
			<AlertDialog open={!!revokeSessionId} onOpenChange={(open) => !open && setRevokeSessionId(null)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{revokeSessionId === currentSessionId ? 'Revoke This Device?' : 'Revoke Session?'}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{revokeSessionId === currentSessionId
								? "This device's session will be revoked. You will be signed out on this browser and need to sign in again."
								: 'That device will be signed out. It will need to sign in again.'}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => revokeSessionId && revokeSessionMutation.mutate({ id: revokeSessionId })}
							disabled={revokeSessionMutation.isPending}
						>
							{revokeSessionMutation.isPending ? <Spinner className="size-4" /> : null}
							Revoke
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
